import React, { FC, MouseEvent, useState, useMemo } from 'react'
import isEqual from 'lodash/isEqual'
import orderBy from 'lodash/orderBy'
import { SortArrowToggle } from 'transfix-ui/components/SortArrowToggle'
import { Table, TableBody, TableCell, TableHead, TableRow } from 'transfix-ui/components/Table'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Box, makeStyles } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { IUserMotorCarrier, IMotorCarrier } from 'apps/fleet-planner/types'
import { hasCarrierId } from 'apps/fleet-planner/utils/motorCarrierHelpers'
import { InfoIcon } from 'transfix-icons'
import { CarriersListFilters } from './CarriersListFilters'
import { CarriersMobileRow } from './CarriersMobileRow'
import { CarriersSortingOptions } from './CarriersSortingOptions'
import { CarriersListRow } from './CarriersTableRow'

interface ICarriersTableProps {
  userMotorCarriers: IUserMotorCarrier[]
  onRemoveCarrier: (motorCarrier: IMotorCarrier) => void
}

const TOP_NAV_HEIGHT = 72

const useCarriersTableStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(3) + TOP_NAV_HEIGHT,
    paddingBottom: theme.spacing(7),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),

    // Keeps the table 100% or less, so the top navbar is still functional
    width: '100%',
    overflow: 'scroll',

    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(5),
      paddingRight: theme.spacing(6),
      paddingLeft: theme.spacing(6),
    },
  },
}))

export const CarriersTable: FC<ICarriersTableProps> = ({ onRemoveCarrier, userMotorCarriers }) => {
  const [sortField, setSortField] = useState<string>('motorCarrier.name')
  const [isReversedSort, setIsReversedSort] = useState<boolean>(false)
  const [filteredCarriers, setFilteredCarriers] = useState<IMotorCarrier[]>([])
  const { isMobile } = useBreakpointContext()

  const sortHandler = (e: MouseEvent<HTMLButtonElement>) => {
    const updatedSortField = e.currentTarget.value
    if (isEqual(sortField, updatedSortField)) {
      // toggle sort order if the heading is clicked again
      setIsReversedSort(prevSortValue => !prevSortValue)
    } else if (isReversedSort) {
      // if a different heading is clicked reset sort order
      setIsReversedSort(false)
    }
    return setSortField(updatedSortField)
  }

  const mobileSortHandler = (updatedSortField: string) => {
    if (isEqual(sortField, updatedSortField)) {
      setIsReversedSort(prevSortValue => !prevSortValue)
    } else if (isReversedSort) {
      setIsReversedSort(false)
    }
    return setSortField(updatedSortField)
  }

  const SortableHeader: React.FC<{ heading: string; value: string }> = ({ heading, value }) => {
    return (
      <button className='sort' value={value} onClick={sortHandler}>
        {heading}
        <SortArrowToggle direction={isReversedSort ? 'asc' : 'desc'} />
      </button>
    )
  }

  const sortedUserMotorCarriers: IUserMotorCarrier[] = useMemo(() => {
    let sorted = orderBy(userMotorCarriers, sortField, isReversedSort ? 'desc' : 'asc')
    if (filteredCarriers.length) {
      sorted = sorted.filter(umc => hasCarrierId(filteredCarriers, umc.motorCarrier.id))
    }
    return sorted
  }, [userMotorCarriers, sortField, isReversedSort, filteredCarriers])

  const tableHeader = (
    <TableHead>
      <TableRow tableCellType='th'>
        {/* Carrier Name */}
        <TableCell>
          <SortableHeader heading='Carrier' value='motorCarrier.name' />
        </TableCell>
        {/* DOT Number */}
        <TableCell>
          <SortableHeader heading='DOT' value='motorCarrier.dotNumber' />
        </TableCell>
        {/* MC Number */}
        <TableCell>
          <SortableHeader heading='MC' value='motorCarrier.mcNumber' />
        </TableCell>
        {/* Team Member Count */}
        <TableCell>
          <SortableHeader heading='Team Members' value='membership.usersCount' />
        </TableCell>
        {/* Transfix Status */}
        <TableCell>
          <Box display='flex' alignItems='center'>
            <span className='pr1'>Transfix Status</span>
            <Tooltip
              position='top'
              type='dark'
              trigger={
                <span className='pt1'>
                  <InfoIcon color='coolGray5' />
                </span>
              }>
              When your carrier has been verified to book Transfix loads, their Transfix status will
              be “Active”.
            </Tooltip>
          </Box>
        </TableCell>
        {/* Date Added */}
        <TableCell>
          <SortableHeader heading='Date Added' value='membership.createdAt' />
        </TableCell>
        <TableCell> </TableCell>
      </TableRow>
    </TableHead>
  )

  const MobileTableBody = () => {
    return (
      <Box>
        {sortedUserMotorCarriers.map(userMotorCarrier => (
          <CarriersMobileRow
            carrier={userMotorCarrier}
            key={userMotorCarrier.motorCarrier.id}
            onRemove={onRemoveCarrier}
          />
        ))}
      </Box>
    )
  }

  const TableBodyRows = () => {
    return (
      <TableBody>
        {sortedUserMotorCarriers.map(userMotorCarrier => (
          <CarriersListRow
            userMotorCarrier={userMotorCarrier}
            key={userMotorCarrier.motorCarrier.id}
            onRemoveCarrier={onRemoveCarrier}
          />
        ))}
      </TableBody>
    )
  }

  const classes = useCarriersTableStyles()

  return (
    <Box className={classes.root}>
      <CarriersListFilters
        motorCarriers={userMotorCarriers.map(umc => umc.motorCarrier)}
        onFilterChange={setFilteredCarriers}
      />
      <Table>
        {isMobile ? <CarriersSortingOptions onChange={mobileSortHandler} /> : tableHeader}
        {isMobile ? <></> : <TableBodyRows />}
      </Table>
      {isMobile && <MobileTableBody />}
    </Box>
  )
}
